import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Contact, Car, Handshake, CornerDownLeft, Command, Users, LifeBuoy, SprayCan, Headphones } from 'lucide-react';
import api from '../lib/api';

const EMPTY = { customers: [], vehicles: [], deals: [], leads: [], bdc_leads: [], post_sale: [], detail: [] };
const GROUPS = ['customers', 'bdc_leads', 'vehicles', 'leads', 'deals', 'post_sale', 'detail'];

const groupMeta = {
  customers: { label: 'Clientes', icon: Contact, route: (id) => `/app/clientes?c=${id}`, color: 'text-primary' },
  vehicles: { label: 'Veículos', icon: Car, route: (id) => `/app/estoque?v=${id}`, color: 'text-tech' },
  leads: { label: 'Leads / CRM', icon: Users, route: (id) => `/app/crm?lead=${id}`, color: 'text-sky-400' },
  bdc_leads: { label: 'Leads do BDC', icon: Headphones, route: (id) => `/app/bdc?lead=${id}`, color: 'text-teal-400' },
  deals: { label: 'Deals', icon: Handshake, route: (id) => `/app/deals?deal=${id}`, color: 'text-emerald-400' },
  post_sale: { label: 'Pós-Venda', icon: LifeBuoy, route: (id) => `/app/post-sale?case=${id}`, color: 'text-orange-400' },
  detail: { label: 'Detail / Clean', icon: SprayCan, route: (id) => `/app/detail?job=${id}`, color: 'text-violet-400' },
};

export const CommandBar = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [res, setRes] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const timer = useRef(null);

  // global Cmd+K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  useEffect(() => {
    if (open) { setTimeout(() => inputRef.current?.focus(), 40); }
    else { setQ(''); setRes(EMPTY); setActive(0); }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setRes(EMPTY); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setRes(data); setActive(0);
      } catch (e) {} finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(timer.current);
  }, [q]);

  const flat = [
    ...(res.customers || []).map((c) => ({ type: 'customers', id: c.id, title: c.nome, sub: [c.telefone, c.email, ...(c.vin_hits || []).map((v) => `VIN …${(v.vin || '').slice(-6)} ${v.desc}`), (c.vehicles || []).length ? `${c.vehicles.length} veículo(s)` : ''].filter(Boolean).join(' · ') })),
    ...(res.vehicles || []).map((v) => ({ type: 'vehicles', id: v.id, title: `${v.ano || ''} ${v.marca || ''} ${v.modelo || ''}`.trim(), sub: `#${v.placa || '—'} · $${Number(v.preco || 0).toLocaleString('en-US')}` })),
    ...(res.leads || []).map((l) => ({ type: 'leads', id: l.id, title: l.nome, sub: [l.etapa, l.vendedor, l.telefone].filter(Boolean).join(' · ') })),
    ...(res.bdc_leads || []).map((b) => ({ type: 'bdc_leads', id: b.id, title: `${b.first_name || ''} ${b.last_name || ''}`.trim() || 'Sem nome', sub: [b.phone, b.email, b.source, b.bdc_agent_name].filter(Boolean).join(' · ') })),
    ...(res.deals || []).map((d) => ({ type: 'deals', id: d.id, title: d.customer_name || d.cliente || d.id, sub: [d.id, d.vehicle_desc || d.veiculo, d.status].filter(Boolean).join(' · ') })),
    ...(res.post_sale || []).map((p) => ({ type: 'post_sale', id: p.id, title: p.cliente, sub: [p.tipo, p.veiculo, p.delivered ? 'Entregue' : p.status, p.createdBy && `aberto por ${p.createdBy}`].filter(Boolean).join(' · ') })),
    ...(res.detail || []).map((j) => ({ type: 'detail', id: j.id, title: `${j.ano || ''} ${j.marca || ''} ${j.modelo || ''}`.trim(), sub: [j.tipo_label, j.worker_name, j.day].filter(Boolean).join(' · ') })),
  ];

  const go = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    navigate(groupMeta[item.type].route(item.id));
  }, [navigate, setOpen]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); go(flat[active]); }
  };

  if (!open) return null;

  let runningIdx = -1;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4" data-testid="command-bar">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-xl glass-strong rounded-2xl shadow-glow-lg overflow-hidden animate-scale-in">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
        <div className="flex items-center gap-3 px-4 h-14 border-b border-foreground/10">
          <Search className="w-5 h-5 text-primary shrink-0" />
          <input
            ref={inputRef}
            data-testid="command-bar-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Buscar por nome, telefone, e-mail, VIN, estoque..."
            className="flex-1 bg-transparent outline-none text-[15px] text-foreground placeholder:text-foreground/35"
          />
          <span className="hidden sm:flex items-center gap-1 text-[10px] text-foreground/40 border border-foreground/10 rounded px-1.5 py-0.5"><Command className="w-3 h-3" />K</span>
        </div>

        <div className="max-h-[52vh] overflow-y-auto py-2">
          {q.trim().length < 2 && (
            <p className="text-center text-sm text-foreground/40 py-10">Digite ao menos 2 caracteres para buscar.</p>
          )}
          {q.trim().length >= 2 && !loading && flat.length === 0 && (
            <p className="text-center text-sm text-foreground/40 py-10">Nenhum resultado para "<span className="text-foreground/70">{q}</span>".</p>
          )}
          {loading && flat.length === 0 && (
            <div className="px-4 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-11 skeleton" />)}</div>
          )}

          {GROUPS.map((g) => {
            const items = flat.filter((f) => f.type === g);
            if (!items.length) return null;
            const M = groupMeta[g];
            return (
              <div key={g} className="px-2 pb-1">
                <p className="px-3 py-1.5 text-[10px] font-bold tracking-[0.18em] text-foreground/40 uppercase">{M.label}</p>
                {items.map((item) => {
                  runningIdx += 1;
                  const idx = runningIdx;
                  const isActive = idx === active;
                  const Ic = M.icon;
                  return (
                    <button
                      key={`${g}-${item.id}`}
                      data-testid={`command-result-${item.id}`}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => go(item)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${isActive ? 'bg-primary/15 shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.3)]' : 'hover:bg-foreground/[0.04]'}`}
                    >
                      <span className={`w-8 h-8 rounded-lg bg-foreground/[0.05] border border-foreground/10 flex items-center justify-center ${M.color}`}><Ic className="w-4 h-4" /></span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
                        {item.sub && <p className="text-[11px] text-foreground/45 truncate">{item.sub}</p>}
                      </div>
                      {isActive && <CornerDownLeft className="w-3.5 h-3.5 text-foreground/40" />}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-4 px-4 h-9 border-t border-foreground/10 text-[10px] text-foreground/35">
          <span className="flex items-center gap-1">↑↓ navegar</span>
          <span className="flex items-center gap-1"><CornerDownLeft className="w-3 h-3" /> abrir</span>
          <span className="flex items-center gap-1">esc fechar</span>
        </div>
      </div>
    </div>
  );
};

export default CommandBar;
